/**
 * Admin Setup -> Purchasing -> Export Templates.
 *
 * Manage company-shared, admin-managed column layouts for the SO/PO Lines
 * export. Admins pick & order columns and save named templates; users then
 * pick a template when downloading the SO Lines export.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Loader2, Plus, Pencil, Trash2, Columns3 } from 'lucide-react';
import { purchasingAPI, POLineExportColumn, POLineExportTemplate } from '../../../lib/api';
import { toast } from 'sonner';
import { ExportTemplateColumnPicker } from './ExportTemplateColumnPicker';

interface FormState {
  name: string;
  columns: string[];
  is_active: boolean;
}

const EMPTY_FORM: FormState = { name: '', columns: [], is_active: true };

export function ExportTemplatesSection() {
  const [templates, setTemplates] = useState<POLineExportTemplate[]>([]);
  const [columns, setColumns] = useState<POLineExportColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<POLineExportTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<POLineExportTemplate | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tplRes, colRes] = await Promise.all([
        purchasingAPI.getExportTemplates({ page_size: 200 }),
        purchasingAPI.getExportColumns(),
      ]);
      const tplData = tplRes.data;
      setTemplates(
        Array.isArray(tplData)
          ? tplData
          : (tplData as { results: POLineExportTemplate[] }).results ?? []
      );
      setColumns(colRes.data ?? []);
    } catch {
      toast.error('Failed to load export templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDialog = (tpl?: POLineExportTemplate) => {
    if (tpl) {
      setEditing(tpl);
      setForm({ name: tpl.name, columns: [...tpl.columns], is_active: tpl.is_active });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const sortTemplates = (list: POLineExportTemplate[]) =>
    [...list].sort((a, b) => a.name.localeCompare(b.name));

  const saveTemplate = async () => {
    if (!form.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (form.columns.length === 0) {
      toast.error('Select at least one column');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await purchasingAPI.updateExportTemplate(editing.id, form);
        setTemplates(prev => sortTemplates(prev.map(t => (t.id === editing.id ? res.data : t))));
        toast.success('Template updated');
      } else {
        const res = await purchasingAPI.createExportTemplate(form);
        setTemplates(prev => sortTemplates([...prev, res.data]));
        toast.success('Template created');
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: Record<string, string[] | string> } };
      const detail = axiosErr.response?.data;
      if (detail && typeof detail === 'object') {
        const msgs = Object.values(detail).flat().join(', ');
        toast.error(msgs || 'Failed to save template');
      } else {
        toast.error('Failed to save template');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    const previous = templates;
    setTemplates(prev => prev.filter(t => t.id !== id));
    setDeleteTarget(null);
    try {
      await purchasingAPI.deleteExportTemplate(id);
      toast.success('Template deleted');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        toast.success('Template already removed');
      } else {
        setTemplates(previous);
        toast.error('Failed to delete template');
      }
    }
  };

  const toggleActive = async (tpl: POLineExportTemplate) => {
    setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, is_active: !t.is_active } : t));
    try {
      await purchasingAPI.updateExportTemplate(tpl.id, { is_active: !tpl.is_active });
    } catch {
      toast.error('Failed to update template');
      await fetchData();
    }
  };

  if (loading && templates.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Columns3 className="h-5 w-5" />
                SO/PO Lines Export Templates
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Saved column layouts users can pick when downloading the SO Lines export
              </p>
            </div>
            <Button size="sm" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-1" />
              Add Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Columns3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No export templates yet</p>
              <p className="text-sm mt-1">Create a template to offer a custom column layout for the SO Lines export</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-28 text-center">Columns</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map(tpl => (
                  <TableRow key={tpl.id}>
                    <TableCell className="font-medium">{tpl.name}</TableCell>
                    <TableCell className="text-center">{tpl.column_count}</TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-2">
                        <Badge
                          variant={tpl.is_active ? 'default' : 'secondary'}
                          className={tpl.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                        >
                          {tpl.is_active ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Switch checked={tpl.is_active} onCheckedChange={() => toggleActive(tpl)} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(tpl)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget(tpl)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Export Template' : 'New Export Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template name</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Buyer Summary"
              />
            </div>
            <ExportTemplateColumnPicker
              allColumns={columns}
              selected={form.columns}
              onChange={cols => setForm({ ...form, columns: cols })}
            />
            <div className="flex items-center justify-between">
              <Label>Status</Label>
              <div className="inline-flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{form.is_active ? 'Enabled' : 'Disabled'}</span>
                <Switch checked={form.is_active} onCheckedChange={checked => setForm({ ...form, is_active: checked })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Delete</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete export template <strong>"{deleteTarget?.name}"</strong>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
